import { useState } from "react";
import {
  Box, Typography, Paper, TextField, InputAdornment, Alert, Stack, Button, Chip,
  IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
} from "@mui/material";
import { DataGrid, type GridColDef, type GridPaginationModel, type GridRowParams } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { customersApi, type CustomerDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { CustomerForm } from "./CustomerForm";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });

export function CustomersPage() {
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CustomerDto | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuRow, setMenuRow] = useState<CustomerDto | null>(null);

  const canCreate = useAuth(s => s.hasPermission("sales.customer.create"));
  const canUpdate = useAuth(s => s.hasPermission("sales.customer.edit"));
  const canDelete = useAuth(s => s.hasPermission("sales.customer.delete"));
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["customers", search, pagination.page, pagination.pageSize],
    queryFn: () => customersApi.list({
      search: search || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => customersApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customers"] }),
  });

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (c: CustomerDto) => { setEditing(c); setFormOpen(true); setMenuAnchor(null); };
  const closeMenu = () => { setMenuAnchor(null); setMenuRow(null); };
  const handleDelete = (c: CustomerDto) => {
    closeMenu();
    if (confirm(`Delete customer "${c.name}"? Blocked if invoices exist.`)) {
      deleteMutation.mutate(c.id);
    }
  };

  const columns: GridColDef<CustomerDto>[] = [
    { field: "code", headerName: "Code", width: 120 },
    { field: "name", headerName: "Name", flex: 1, minWidth: 200 },
    { field: "uhid", headerName: "UHID", width: 140 },
    { field: "phone", headerName: "Phone", width: 130 },
    { field: "email", headerName: "Email", width: 200 },
    {
      field: "creditLimit", headerName: "Credit limit", width: 130, type: "number",
      valueFormatter: (v) => (v == null ? "—" : inr.format(Number(v))),
    },
    {
      field: "isActive", headerName: "Status", width: 100,
      renderCell: (p) => (
        <Chip size="small" label={p.value ? "Active" : "Inactive"} color={p.value ? "success" : "default"} />
      ),
    },
    {
      field: "actions", headerName: "", width: 60, sortable: false, filterable: false,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuRow(p.row); }}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Customers</Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New Customer
          </Button>
        )}
      </Stack>

      <Paper sx={{ p: 2 }}>
        <TextField
          placeholder="Search by code, name, phone or UHID"
          fullWidth size="small" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2 }}
        />
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {deleteMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowDoubleClick={(p: GridRowParams<CustomerDto>) => canUpdate && openEdit(p.row)}
          />
        </Box>
      </Paper>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        {canUpdate && (
          <MenuItem onClick={() => menuRow && openEdit(menuRow)}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem onClick={() => menuRow && handleDelete(menuRow)}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText sx={{ color: "error.main" }}>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <CustomerForm open={formOpen} onClose={() => setFormOpen(false)} customer={editing} />
    </Box>
  );
}
